import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { resetCheck, resetGroupPost, errText } from "@/lib/fbLabApi";

const money = (h) => (h ? `${h.currency || ""} $${Number(h.amount).toLocaleString()} · ${h.bidder || ""}` : "no bids");

function Tick({ id, checked, onChange, children }) {
  return (
    <label className="flex items-start gap-2 text-sm"><Checkbox data-testid={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" /><span>{children}</span></label>
  );
}

function ResetDialog({ open, onOpenChange, info, onGo, busy }) {
  const [a, setA] = useState(false);
  const [b, setB] = useState(false);
  const test = info.post.test;
  const blocked = !test && info.blockers.length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reset-group-dialog">
        <DialogHeader>
          <DialogTitle>Reset Facebook Group post {test ? "(TEST)" : "(LIVE)"}</DialogTitle>
          <DialogDescription>The current post <a className="underline" href={info.post.url} target="_blank" rel="noreferrer">on Facebook</a> stops syncing. Its {info.post.bids} recorded bid(s), high bid, extensions and audit history are archived (never deleted) and stay in Previous group posts. The next post starts with zero bids. Nothing is changed on Facebook.</DialogDescription>
        </DialogHeader>
        {blocked && <ul data-testid="reset-blockers" className="text-sm text-red-700 list-disc pl-5">{info.blockers.map((x) => <li key={x}>{x}</li>)}</ul>}
        {!blocked && (test
          ? <Tick id="reset-confirm-test" checked={a} onChange={setA}>I understand the old TEST post may stay on Facebook but will no longer sync comments or send texts.</Tick>
          : <div className="space-y-2">
              <Tick id="reset-confirm-deleted" checked={a} onChange={setA}>I deleted this post on Facebook myself.</Tick>
              <Tick id="reset-confirm-live" checked={b} onChange={setB}>I confirm resetting a LIVE auction post.</Tick>
            </div>)}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="reset-group-confirm" disabled={blocked || busy || !a || (!test && !b)} className="bg-red-700 hover:bg-red-800 text-white"
            onClick={() => onGo(test ? { confirm_test_reset: true } : { confirm_facebook_deleted: true, confirm_live: true })}>Reset group post</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetGroupPost({ hid, onChanged }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => resetCheck(hid).then(setInfo).catch(() => setInfo(null)), [hid]);
  useEffect(() => { load(); }, [load]);
  if (!info) return null;
  const go = async (body) => {
    setBusy(true);
    try { await resetGroupPost(hid, body); toast.success("Group post reset. Status is Not Posted — you can post to the Facebook Group again."); setOpen(false); load(); onChanged?.(); }
    catch (e) { toast.error(errText(e)); load(); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="reset-group-post" className="space-y-2 border-t border-stone-100 pt-2">
      {info.post && <Button data-testid="reset-group-open" variant="outline" className="w-full border-red-300 text-red-800" onClick={() => setOpen(true)}><RotateCcw className="h-4 w-4 mr-1.5" />Reset Group Post</Button>}
      {info.post && open && <ResetDialog open={open} onOpenChange={setOpen} info={info} onGo={go} busy={busy} />}
      {info.history.length > 0 && (
        <details data-testid="group-post-history" className="text-xs text-stone-600">
          <summary className="cursor-pointer">Previous group posts ({info.history.length})</summary>
          <ul className="mt-1 space-y-1">{info.history.map((h) => (
            <li key={h.id} data-testid={`group-post-history-${h.id}`}><a className="underline" href={h.post_url} target="_blank" rel="noreferrer">Post</a> · reset {h.superseded_at?.slice(0, 16).replace("T", " ")} UTC · {h.superseded_reason === "test_reset" ? "TEST reset" : "deleted on Facebook"} · {h.bids} bid(s) · high {money(h.high)}</li>
          ))}</ul>
        </details>
      )}
    </div>
  );
}
